import { AnalyticsClient } from '../../../analytics/analyticsClient';
import type { DandyAnalyticsEventSchemaType } from '@orthly/analytics/dist/browser';
import { PracticeScreen } from '@orthly/dentin';
import type { MiniWindowPendingAnimation } from '@orthly/ui';
import { MiniWindowVariant, QueryString, useScreenIsMobile } from '@orthly/ui';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';

export interface StandaloneCSTURLParams {
    // this is the source
    platform?: 'trios' | 'chairside';
    group?: string;
    liveScanReviewOrder?: string;
    prefId?: string;
}

const windowVariantAtom = atom<MiniWindowVariant>(MiniWindowVariant.Closed);
const windowPendingAnimationAtom = atom<MiniWindowPendingAnimation>('move');
const platformAtom = atom<'portal' | 'uploader' | 'chairside'>('portal');

export const useChatWindow = (source?: DandyAnalyticsEventSchemaType['Practice - CST Opened']['source']) => {
    const [windowVariant, setWindowVariant] = useAtom(windowVariantAtom);
    const [windowPendingAnimation, setWindowPendingAnimation] = useAtom(windowPendingAnimationAtom);
    const platform = useAtomValue(platformAtom);
    const navigate = useNavigate();
    const isMobile = useScreenIsMobile();

    const setWindowVariantWithAnimation = React.useCallback(
        (variant: MiniWindowVariant, pendingAnimation: MiniWindowPendingAnimation = 'none') => {
            setWindowVariant(variant);
            setWindowPendingAnimation(pendingAnimation);
        },
        [setWindowPendingAnimation, setWindowVariant],
    );

    const maximizeWindow = React.useCallback(
        () => setWindowVariantWithAnimation(MiniWindowVariant.Maximized, 'none'),
        [setWindowVariantWithAnimation],
    );
    const closeWindow = React.useCallback(
        () => setWindowVariantWithAnimation(MiniWindowVariant.Closed, 'move'),
        [setWindowVariantWithAnimation],
    );
    const openWindow = React.useCallback(() => {
        if (isMobile) {
            navigate(`/${PracticeScreen.chat}`);
        } else {
            setWindowVariantWithAnimation(MiniWindowVariant.Maximized, 'move');
        }

        if (source) {
            AnalyticsClient.track('Practice - CST Opened', { source });
        }
    }, [navigate, isMobile, setWindowVariantWithAnimation, source]);

    return {
        closeWindow,
        maximizeWindow,
        openWindow,
        platform,
        setWindowPendingAnimation,
        setWindowVariantWithAnimation,
        windowPendingAnimation,
        windowVariant,
    };
};

export const useStandaloneChatWindow = () => {
    const location = useLocation();
    const setPlatform = useSetAtom(platformAtom);
    const setWindowPendingAnimation = useSetAtom(windowPendingAnimationAtom);
    const [windowVariant, setWindowVariant] = useAtom(windowVariantAtom);

    React.useEffect(() => {
        const searchParams: StandaloneCSTURLParams = QueryString.parse(location.search);
        AnalyticsClient.track('Practice - CST Opened', { source: searchParams.platform });
        setPlatform(searchParams.platform === 'chairside' ? 'chairside' : 'uploader');
        if (windowVariant === MiniWindowVariant.Closed) {
            setWindowVariant(MiniWindowVariant.Maximized);
            setWindowPendingAnimation('move');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
};
